/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { computed } from '@ember/object';
import { dropTask, enqueueTask } from 'ember-concurrency';
import TransactionalMessageModel from 'cio/models/transactional-message';
import FormValidations from 'cio/utils/form-validations';
import TagModel from 'cio/models/tag';
import isDestroyed from 'cio/utils/is-destroyed';

const MAX_NAME_LENGTH = 191;

const Validations = {
  name(value: string) {
    if (!value?.trim()) {
      return 'Name cannot be empty';
    }
    if ([...value].length > MAX_NAME_LENGTH) {
      return 'Name cannot be longer than 191 characters';
    }

    return true;
  },
};

interface TransactionalMessageFormArgs {
  readOnly: boolean;
  message: TransactionalMessageModel;
  saveButtonText: string;
  footerClassNames: string;
  onSave: (message: TransactionalMessageModel) => unknown;
  onClose?: () => unknown;
}

type EditableMessageAttrs = {
  name: string;
  description: string;
  tags: TagModel[];
};

export default class TransactionalMessageForm extends Component<TransactionalMessageFormArgs> {
  validations: FormValidations;

  @tracked messageAttrs: EditableMessageAttrs;

  constructor(owner: unknown, args: TransactionalMessageFormArgs) {
    super(owner, args);

    this.messageAttrs = {
      name: this.args.message.name,
      description: this.args.message.description,
      tags: (this.args.message.tags.content as TagModel[]).toArray(),
    };

    this.validations = FormValidations.create({
      validations: Validations,
      context: this,
      modelKey: 'messageAttrs',
    });
  }

  willDestroy() {
    super.willDestroy();

    if (this.args.message != null && !isDestroyed(this.args.message)) {
      this.args.message.rollbackAttributes();
    }
  }

  @computed('saveMessageTask.isRunning', 'removeTagTask.isRunning', 'addTagTask.isRunning')
  get inProgress() {
    return this.saveMessageTask.isRunning || this.removeTagTask.isRunning || this.addTagTask.isRunning;
  }

  saveMessageTask = dropTask(async (ev: Event) => {
    ev.preventDefault();
    await this.validations.validateModel();

    try {
      this.args.message.setProperties(this.messageAttrs);
      await this.args.onSave(this.args.message);
    } catch {}
  });

  addTagTask = enqueueTask(async (tag: TagModel) => {
    this.messageAttrs = { ...this.messageAttrs, tags: [...this.messageAttrs.tags, tag] };

    if (!this.args.message.get('isNew')) {
      await this.args.message.addTags([tag]);
    }
  });

  removeTagTask = enqueueTask(async (tag: TagModel) => {
    this.messageAttrs = { ...this.messageAttrs, tags: this.messageAttrs.tags.filter((t) => t.id !== tag.id) };

    if (!this.args.message.get('isNew')) {
      await this.args.message.removeTag(tag);
    }
  });
}
